<template>
    <div class="address-container" :class="{ 'fields-compact': fieldStyle == FieldStyle.Compact }">
        <div class="form-row">
            <ValidatedInput :id="id" :value="address.countryString" label="Country"
                :accessibleLabel="countryPlaceholder" :name="name + '.Address.CountryString'" class="rd-w-full"
                :isRequired="required" :maxLength="50">
                <template #input="inputProps">
                    <VueMultiselect :id="id + '_' + name + '.Address.CountryString'" label="text" track-by="value"
                        select-label="" selected-label="" deselect-label="" class="multiselect-element"
                        :placeholder="countryPlaceholder" v-model="selectedCountry"
                        :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                        :aria-invalid="inputProps.isInvalid" :options="countries"
                        :autocomplete="isShipping ? 'shipping country' : 'billing country'" />
                </template>
            </ValidatedInput>
        </div>

        <template v-if="selectedCountry?.value === 'US'">
            <div class="form-row"
                :class="[{ 'rd-mt-4': (fieldStyle != FieldStyle.Compact && !isModalDisplay) }, { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]">
                <ValidatedInput :id="id" :value="smartAddress" :label="address1Label"
                    :accessibleLabel="address1Placeholder" class="rd-w-full" :name="name + '.Address.Address1'"
                    :isRequired="required" :maxLength="75">
                    <template #input="inputProps">
                        <template v-if="showAddressSelect">
                            <VueMultiselect ref="multiselect" :id="id + '_' + name + '.Address.Address1'"
                                :placeholder="address1Placeholder" track-by="street" select-label="" selected-label=""
                                deselect-label="" class="multiselect-element" v-model="smartAddress"
                                :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                                :aria-invalid="inputProps.isInvalid" :options="streets" :internal-search="false"
                                @search-change="findAddress" @select="selectAddress" :custom-label="customLabel"
                                :showNoOptions="true" :showNoResults="true"
                                :autocomplete="isShipping ? 'shipping address-line1' : 'billing address-line1'">
                                <template #singleLabel="props">
                                    {{ props.option.street }}
                                </template>
                                <template #noOptions>
                                    <span class="rd-font-medium">No results found.</span>
                                </template>
                                <template #noResult>
                                    <button type="button" class="address-no-results" @click="closeAddressSearch()">
                                        No results found. Click here to enter your address manually.
                                    </button>
                                </template>
                            </VueMultiselect>
                        </template>
                        <template v-else>
                            <input type="text" v-model="address.address1" :placeholder="address1Placeholder"
                                :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                                :aria-invalid="inputProps.isInvalid" :id="id + '_' + name + '.Address.Address1'"
                                :autocomplete="isShipping ? 'shipping address-line1' : 'billing address-line1'" />
                        </template>
                    </template>
                </ValidatedInput>
            </div>
        </template>

        <template v-if="selectedCountry?.value === 'US'">
            <DomesticAddress :id="id" :name="name" :address="address" :showAddress2="showAddress2" :states="states"
                :required="required" :address2Label="address2Label" :address2Placeholder="address2Placeholder"
                :cityLabel="cityLabel" :cityPlaceholder="cityPlaceholder" :stateLabel="stateLabel"
                :statePlaceholder="statePlaceholder" :postalLabel="postalLabel" :postalPlaceholder="postalPlaceholder"
                @update-address="updateAddress" :is-shipping="isShipping" />
        </template>

        <template v-if="selectedCountry?.value === 'CA'">
            <CanadaAddress :id="id" :name="name" :address="address" :showAddress2="showAddress2" :states="states"
                :required="required" :address1Label="address1Label" :address1Placeholder="address1Placeholder"
                :address2Label="address2Label" :address2Placeholder="address2Placeholder" :cityLabel="cityLabel"
                :cityPlaceholder="cityPlaceholder" :stateLabel="stateLabel" :statePlaceholder="statePlaceholder"
                :postalLabel="postalLabel" :postalPlaceholder="postalPlaceholder" @update-address="updateAddress"
                :is-shipping="isShipping" />
        </template>

        <template v-if="selectedCountry?.value !== 'CA' && selectedCountry?.value !== 'US'">
            <InternationalAddress :id="id" :name="name" :address="address" :showAddress2="showAddress2"
                :required="required" :address1Label="address1Label" :address1Placeholder="address1Placeholder"
                :address2Label="address2Label" :address2Placeholder="address2Placeholder" :cityLabel="cityLabel"
                :cityPlaceholder="cityPlaceholder" :postalLabel="postalLabel" :postalPlaceholder="postalPlaceholder"
                @update-address="updateAddress" :is-shipping="isShipping" />
        </template>
    </div>
</template>

<script lang="ts">
import ValidatedInput from "@/builder/components/input/ValidatedInput.vue";
import { FieldStyle, PartialDisplayType } from "@/builder/enums";
import { Address, AddressRequest, Errors, LookupResponse } from "@/interfaces";
import { publicApiService } from "@/services/publicApiService";
import { ErrorUtility } from "@/utilities/ErrorUtility";
import { PropType, Ref, computed, defineComponent, inject } from "vue";
import VueMultiselect from "vue-multiselect";
import { PageDesign } from "../../interfaces";
import { useRenderStore } from "../../stores/renderStore";
import CanadaAddress from "./CanadaAddress.vue";
import DomesticAddress from "./DomesticAddress.vue";
import InternationalAddress from "./InternationalAddress.vue";

export default defineComponent({
    name: "AddressInputComponent",
    props: {
        id: {
            type: String,
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
        passedAddress: {
            type: Object as PropType<Address | AddressRequest | null>,
            required: false,
        },
        showAddress2: {
            type: Boolean,
            required: false,
            default: true,
        },
        required: {
            type: Boolean,
            required: false,
            default: false,
        },
        address1Label: {
            type: String,
            required: false,
            default: "",
        },
        address1Placeholder: {
            type: String,
            required: false,
            default: "Address Line 1",
        },
        address2Label: {
            type: String,
            required: false,
            default: "",
        },
        address2Placeholder: {
            type: String,
            required: false,
            default: "Address Line 1",
        },
        cityLabel: {
            type: String,
            required: false,
            default: "",
        },
        cityPlaceholder: {
            type: String,
            required: false,
            default: "City",
        },
        stateLabel: {
            type: String,
            required: false,
            default: "",
        },
        statePlaceholder: {
            type: String,
            required: false,
            default: "State",
        },
        postalLabel: {
            type: String,
            required: false,
            default: "",
        },
        postalPlaceholder: {
            type: String,
            required: false,
            default: "Postal",
        },
        countryLabel: {
            type: String,
            required: false,
            default: "",
        },
        countryPlaceholder: {
            type: String,
            required: false,
            default: "Country",
        },
        isShipping: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    components: {
        DomesticAddress,
        InternationalAddress,
        CanadaAddress,
        ValidatedInput,
        VueMultiselect,
    },
    emits: ["update-address"],
    setup() {
        const renderStore = useRenderStore();
        const publicApi = inject('publicApiService') as publicApiService;
        const pageDesign = inject("pageDesign") as Ref<PageDesign>;
        const fieldStyle = computed(() => {
            return pageDesign.value.fieldStyle;
        });

        return { renderStore, publicApi, fieldStyle, FieldStyle };
    },
    data() {
        return {
            address: {
                address1: null as string | null,
                address2: null as string | null,
                city: null as string | null,
                state: null as string | null,
                postal: null as string | null,
                countryString: null as string | null,
            } as Address,
            smartAddress: null as object | null,
            selectedCountry: null as LookupResponse | null,
            states: [] as LookupResponse[],
            countries: [] as LookupResponse[],
            streets: [] as any[],
            findAddressTimeout: null as ReturnType<typeof setTimeout> | null,
            currentSearch: null as string | null,
            showAddressSelect: true
        };
    },
    computed: {
        isModalDisplay() {
            return this.renderStore.page.partialDisplayType === PartialDisplayType.Modal;
        }
    },
    mounted() {
        this.getCountries();
        this.getStates("us");

        //set address if passed in
        if (this.passedAddress && this.passedAddress.address1 !== null && this.renderStore.previewMode) {
            this.address.address1 = this.passedAddress.address1;
            this.address.address2 = this.passedAddress.address2;
            this.address.city = this.passedAddress.city;
            this.address.state = this.passedAddress.state;
            this.address.postal = this.passedAddress.postal;
            this.address.countryString = this.passedAddress.countryString;

            this.smartAddress = {
                street: this.passedAddress.address1,
                secondary: this.passedAddress.address2,
                city: this.passedAddress.city,
                state: this.passedAddress.state,
                postal: this.passedAddress.postal,
            };
        }
    },
    methods: {
        customLabel(option) {
            return `${option.street} ${option.city}, ${option.state} ${option.postal}`
        },

        selectAddress(selection): void {
            this.address.address1 = selection.street;
            this.address.address2 = selection.secondary;
            this.address.city = selection.city;
            this.address.state = selection.state;
            this.address.postal = selection.postal;
        },

        updateAddress(newAddress: Address): void {
            this.address = newAddress;
        },

        async findAddress(query): Promise<any> {
            if (!query) {
                return;
            }

            if (this.findAddressTimeout) {
                clearTimeout(this.findAddressTimeout);
            }

            this.findAddressTimeout = setTimeout(async () => {
                this.currentSearch = query;
                await handleRequest(query);
            }, 250);

            const handleRequest = async (query) => {
                try {
                    const results = await this.publicApi.searchAddress(query);
                    this.streets = results;
                } catch (err) {
                    console.log(err);
                }
            };
        },

        closeAddressSearch(): void {
            const multiselect = this.$refs.multiselect as any;
            if (multiselect) {
                //reset address
                this.address.address1 = null;
                this.address.address2 = null;
                this.address.city = null;
                this.address.state = null;
                this.address.postal = null;

                //set current search as address1
                this.address.address1 = this.currentSearch;
                this.smartAddress = {
                    street: this.currentSearch,
                    secondary: null,
                    city: null,
                    state: null,
                    postal: null,
                }

                multiselect.deactivate();

                this.showAddressSelect = false;
            }
        },

        getCountries(): void {
            this.publicApi
                .getCountries()
                .then((response) => {
                    this.countries = response;
                    this.selectedCountry = this.countries.filter(
                        (x) => x.value === "US"
                    )[0];
                })
                .catch((error) => {
                    const handledErrors: Errors =
                        ErrorUtility.HandleError(error);
                });
        },

        getStates(countryCode: string | null): void {
            this.publicApi
                .getStates(countryCode)
                .then((response) => {
                    this.states = response;
                })
                .catch((error) => {
                    const handledErrors: Errors =
                        ErrorUtility.HandleError(error);
                });
        }
    },
    watch: {
        selectedCountry: function (newVal) {
            this.address.countryString = newVal?.value ?? null;
            this.getStates(newVal?.value ?? null);
        },
        address: {
            handler(val: Address) {
                this.$emit("update-address", val);
            },
            deep: true
        }
    },
});
</script>
